import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import App from './App';
import registerServiceWorker from './registerServiceWorker';
import './globals.css'; // Single static import — loads synchronously with the app
import { SESSION_ENDED_HOLD_KEY } from './sessionEndedEvents';

// Reload should send the user to login (hold flag is only for the current document)
try {
    const nav = window.performance?.getEntriesByType?.('navigation')?.[0];
    if (nav && nav.type === 'reload') {
        sessionStorage.removeItem(SESSION_ENDED_HOLD_KEY);
    } else if (window.performance?.navigation?.type === 1) {
        sessionStorage.removeItem(SESSION_ENDED_HOLD_KEY);
    }
} catch (_) { /* ignore */ }

// Set brand theme on <html> BEFORE React renders — no flash of unstyled content
const domain = window.location.host.split('.').slice(-2).join('.').toLowerCase();
const host = window.location.host.toLowerCase();

const isNlsrx = domain === "rxhacker.com" || 
    domain === "localhost:5464" || 
    host === "tempfilter.23byt.es" || 
                host === "reporting.nulifespanrx.com";

const brandClass = isNlsrx ? 'nlsrx' : 'glic';
document.documentElement.classList.add(brandClass);

// Re-apply brand class if tweakcn or any external tool replaces the <html> class attribute
const brandObserver = new MutationObserver(() => {
    if (!document.documentElement.classList.contains(brandClass)) {
        document.documentElement.classList.add(brandClass);
    }
});
brandObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
});

// Global fetch interceptor — injects X-User-Context on every outgoing request
// Covers all direct fetch() calls across chart components, services, and hooks
const _nativeFetch = window.fetch;
window.fetch = function (url, options = {}) {
    try {
        let userContext = 'N/A';
        const raw = localStorage.getItem('user');
        if (raw) {
            const u = JSON.parse(raw);
            if (u && u.email) {
                userContext = [
                    `email:${u.email}`,
                    `role:${u.userrole ?? ''}`,
                    `username:${u.username ?? ''}`,
                    `id:${u.id ?? ''}`,
                    `wholesaler:${u.wholesaler ?? ''}`,
                    `wholesaler_Id:${u.wholesaler_Id ?? ''}`,
                    `distributor_UserId:${u.distributor_UserId ?? ''}`,
                ].join(' | ');
            }
        }
        options.headers = {
            ...options.headers,
            'X-User-Context': userContext,
        };
    } catch (_) { }
    return _nativeFetch.call(this, url, options);
};

// Create React Query client
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            // Data is considered fresh for 5 minutes
            staleTime: 5 * 60 * 1000,
            // Cache stays in memory for 10 minutes after last use
            cacheTime: 10 * 60 * 1000,
            // Don't refetch on window focus by default
            refetchOnWindowFocus: false,
            // Retry failed requests once
            retry: 1,
            // Don't refetch on component mount if data is fresh
            refetchOnMount: false,
        },
    },
});

// setup fake backend
//import { configureFakeBackend } from './_helpers';
const baseUrl = document.getElementsByTagName('base')[0]?.getAttribute('href');
const rootElement = document.getElementById('root');
//configureFakeBackend();

if (rootElement) {
    const root = createRoot(rootElement);
    setTimeout(() => {
        root.render(
            <QueryClientProvider client={queryClient}>
                <App />
                {/* React Query DevTools - only shows in development */}
                <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
            </QueryClientProvider>
        );
        //registerServiceWorker();
    }, 400);
}
